import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { buildCompanySyncSocketUrl, getCompanySyncClientId } from "../apis/companySyncClient";
import { ROUTE_PATHS } from "../routes/routePath";

const cookies = new Cookies();

function endSession() {
  cookies.remove("token", { path: "/" });
  cookies.remove("userId", { path: "/" });
  cookies.remove("userFullName", { path: "/" });
  cookies.remove("defaultcompany", { path: "/" });
  cookies.remove("defaulttenant", { path: "/" });
  cookies.remove("tenantEnabled", { path: "/" });
  const authPaths = [
    ROUTE_PATHS.LOGIN,
    ROUTE_PATHS.ADMIN_LOGIN,
    ROUTE_PATHS.TWO_FA,
    ROUTE_PATHS.FORGOT_PASSWORD,
    ROUTE_PATHS.RESET_PASSWORD,
  ];
  if (!authPaths.includes(window.location.pathname)) {
    window.location.href = ROUTE_PATHS.LOGIN;
  }
}

/**
 * Listens for the selected company, company-record changes, and a forced sign-out.
 * This tab ignores its own active-company echo because it already switched the dropdown.
 */
export function useCompanySync(tenantKey, onActiveCompanyRef) {
  const queryClient = useQueryClient();
  const token = cookies.get("token") || "";

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL;
    if (!token || !apiBase) return undefined;

    let socket = null;
    let stopped = false;
    let attempt = 0;
    let retryTimer = null;
    let pingTimer = null;

    const clearTimers = () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (pingTimer) clearInterval(pingTimer);
      retryTimer = null;
      pingTimer = null;
    };

    const connect = () => {
      if (stopped) return;
      const currentToken = cookies.get("token");
      if (!currentToken) return;

      socket = new WebSocket(
        buildCompanySyncSocketUrl(apiBase, currentToken, tenantKey),
      );

      socket.onopen = () => {
        attempt = 0;
        pingTimer = setInterval(() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send("ping");
          }
        }, 25000);
      };

      socket.onmessage = (event) => {
        if (event.data === "pong") return;
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message?.type === "session.ended") {
          endSession();
          return;
        }
        if (
          message.originClientId &&
          message.originClientId === getCompanySyncClientId()
        ) {
          return;
        }
        if (message?.type === "active-company.changed") {
          const companyId = Number(message.companyId);
          if (companyId) onActiveCompanyRef?.current?.(companyId);
          return;
        }
        if (message?.type !== "company.changed") return;
        queryClient.invalidateQueries({ queryKey: ["companyLists"] });
      };

      socket.onclose = () => {
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = null;
        if (stopped) return;
        attempt += 1;
        const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      stopped = true;
      clearTimers();
      if (socket && socket.readyState < WebSocket.CLOSING) {
        socket.close();
      }
    };
  }, [tenantKey, token, queryClient, onActiveCompanyRef]);
}
